import { InputNumber, Modal, message, Switch } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import type { CommunityInviteChannel } from '@/services/inviteAdminContract';
import { authStore } from '@/stores';
import { inviteAdminStore } from '@/stores/inviteAdminStore';

export const CommunityInvitePanel = observer(() => {
  const [editing, setEditing] = useState<CommunityInviteChannel | null>(null);
  const [capacity, setCapacity] = useState(50);
  const [enabled, setEnabled] = useState(true);
  const columns: Column<CommunityInviteChannel>[] = [
    {
      id: 'name',
      header: '社区',
      accessor: (row) => (
        <div>
          {row.name}
          <div className="text-xs text-muted-foreground">{row.slug}</div>
        </div>
      ),
    },
    {
      id: 'code',
      header: '团体邀请码',
      accessor: (row) => <code className="select-all">{row.inviteCode}</code>,
    },
    {
      id: 'capacity',
      header: '已激活 / 名额',
      accessor: (row) => `${row.usedCount} / ${row.maxUses}`,
    },
    {
      id: 'enabled',
      header: '状态',
      accessor: (row) => (row.enabled ? '开放' : '暂停'),
    },
    {
      id: 'actions',
      header: '操作',
      accessor: (row) =>
        authStore.can(ADMIN_PERMISSIONS.invitesWrite) ? (
          <Button
            variant="outline"
            disabled={inviteAdminStore.busy}
            onClick={() => {
              setEditing(row);
              setCapacity(row.maxUses);
              setEnabled(row.enabled);
            }}
          >
            调整名额 / 暂停
          </Button>
        ) : (
          '只读'
        ),
    },
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        同码可多人使用，成功激活新账号才占用名额。暂停后历史激活记录保留。
      </p>
      <DataTable
        columns={columns}
        data={inviteAdminStore.channels}
        rowKey="id"
        loading={inviteAdminStore.loading}
        emptyMessage="暂无团体邀请码"
      />
      {inviteAdminStore.nextChannelId && (
        <Button
          disabled={inviteAdminStore.loading}
          onClick={() => {
            void inviteAdminStore
              .loadMoreChannels()
              .catch(() => message.error('加载团体邀请码失败，请重试'));
          }}
        >
          加载更多
        </Button>
      )}
      <Modal
        title="调整团体邀请码"
        open={editing !== null}
        confirmLoading={inviteAdminStore.busy}
        onCancel={() => {
          if (!inviteAdminStore.busy) setEditing(null);
        }}
        onOk={async () => {
          if (!editing || !authStore.can(ADMIN_PERMISSIONS.invitesWrite))
            return;
          try {
            await inviteAdminStore.updateChannel({
              slug: editing.slug,
              maxUses: capacity,
              enabled,
            });
            setEditing(null);
            message.success('团体邀请码已更新');
          } catch {
            message.error('更新失败，请检查名额是否低于已激活人数后重试');
          }
        }}
      >
        <div className="space-y-4 py-4">
          <label
            htmlFor="group-capacity-edit"
            className="flex items-center justify-between gap-4"
          >
            激活名额
            <InputNumber
              id="group-capacity-edit"
              aria-label="激活名额"
              min={editing?.usedCount || 1}
              max={1000000}
              precision={0}
              value={capacity}
              onChange={(value) => {
                if (value !== null) setCapacity(value);
              }}
            />
          </label>
          <label
            htmlFor="group-enabled-edit"
            className="flex items-center justify-between gap-4"
          >
            允许新账号激活
            <Switch
              id="group-enabled-edit"
              checked={enabled}
              onChange={setEnabled}
            />
          </label>
        </div>
      </Modal>
    </div>
  );
});
